/**
 * One value, chosen from a list too long to scroll (docs/03 §5, docs/04 §3j).
 *
 * docs/03 has said "selects searchable ≥8 options" since the beginning, and the
 * app had no component for it: `SelectField` is a native `<select>` and
 * `MultiSelectPicker` is a checkbox list for choosing several. Punch 4.1's time
 * zone is 418 options, which is a native select nobody can use.
 *
 * ─── THIS ONE IS A REAL COMBOBOX, AND THE MENTION CHOOSER IS NOT ──────────
 *
 * `MentionTextArea` deliberately avoids `role="combobox"`, because ARIA-in-HTML
 * allows a `<textarea>` no role but `textbox` and `aria-expanded` is not a state
 * `textbox` supports. Neither objection applies to an `<input>`: combobox is a
 * valid role for it and carries `aria-expanded` properly. So this uses the whole
 * ARIA 1.2 pattern — combobox owning a listbox, `aria-activedescendant` moving a
 * visual highlight without moving focus — and the two components differ because
 * their elements differ, not because two people made two choices.
 *
 * ─── WHAT IT REFUSES TO DO ────────────────────────────────────────────────
 *
 * It never accepts free text. The input is a FILTER, not the value: closing
 * without a pick restores the label of whatever was already chosen. A control
 * that quietly kept an unmatched string would be a text field wearing a
 * picker's clothes, and 4.1's whole point is that `America/New_Yrok` must be
 * impossible rather than merely unlikely.
 */

import { clsx } from 'clsx';
import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

export interface SearchSelectOption {
  readonly value: string;
  readonly label: string;
  /** A second line — an offset, a city, whatever tells two similar rows apart. */
  readonly detail?: string | undefined;
}

const MAX_SHOWN = 50;

export function SearchSelect({
  label,
  value,
  options,
  onChange,
  placeholder,
  hint,
  error,
  required,
  emptyLabel = 'No matches',
  clearLabel,
  onSearchChange,
}: {
  readonly label: string;
  /** The chosen option's value, or null. */
  readonly value: string | null;
  readonly options: readonly SearchSelectOption[];
  readonly onChange: (next: string | null) => void;
  readonly placeholder?: string | undefined;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly required?: boolean | undefined;
  readonly emptyLabel?: string | undefined;
  /** Present ⇒ the value can be cleared, and this names the act. */
  readonly clearLabel?: string | undefined;
  /**
   * The filter text, as it changes. For a parent that wants to OFFER a
   * query-derived option (`PartyPicker`'s "use this as free text").
   *
   * This does not weaken the rule above. The typed string still never becomes
   * the value on its own: the parent must turn it into a real option, and the
   * person must pick it. What is forbidden is keeping an unmatched string
   * silently, not letting a caller see what was typed.
   */
  readonly onSearchChange?: ((search: string) => void) | undefined;
}): ReactNode {
  const id = useId();
  const listId = `${id}-list`;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const chosen = options.find((option) => option.value === value) ?? null;

  /**
   * FILTER FIRST, THEN CAP. The order is the whole contract: the search reaches
   * every option, and `MAX_SHOWN` only bounds how many rows are drawn. A control
   * that capped the DATA and searched the cap is the RM-052 defect, and this is
   * the component that must not have it.
   */
  const { shown, hidden } = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matches =
      needle === ''
        ? options
        : options.filter(
            (option) =>
              option.label.toLowerCase().includes(needle) ||
              option.value.toLowerCase().includes(needle) ||
              (option.detail ?? '').toLowerCase().includes(needle),
          );
    return { shown: matches.slice(0, MAX_SHOWN), hidden: Math.max(0, matches.length - MAX_SHOWN) };
  }, [options, search]);

  const close = (): void => {
    setOpen(false);
    setSearch('');
    onSearchChange?.('');
    setActive(0);
  };

  const pick = (option: SearchSelectOption): void => {
    onChange(option.value);
    close();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => {
        if (shown.length === 0) return 0;
        return (current + step + shown.length) % shown.length;
      });
      return;
    }
    if (event.key === 'Enter' && open) {
      const option = shown[active];
      if (option !== undefined) {
        event.preventDefault();
        pick(option);
      }
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-meta font-medium text-ink2">
        {label}
        {required === true ? (
          <span aria-hidden className="text-red-fg">
            {' '}
            *
          </span>
        ) : null}
      </label>

      <div className="relative">
        <input
          id={id}
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && shown[active] !== undefined ? `${id}-o${String(active)}` : undefined
          }
          aria-invalid={error !== undefined || undefined}
          className={clsx(
            'keeper-ignore min-h-11 w-full rounded-ctl border bg-surface px-3 text-body text-ink',
            error !== undefined ? 'border-red-fg' : 'border-control-line',
          )}
          placeholder={placeholder}
          /*
           * The input SHOWS the chosen label and BECOMES the filter while open.
           * One box rather than a box plus a separate trigger: the thing you
           * type into and the thing that displays the answer are the same
           * control, which is what makes it feel like a select.
           */
          value={open ? search : (chosen?.label ?? '')}
          onChange={(event) => {
            setSearch(event.target.value);
            onSearchChange?.(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onBlur={() => {
            // A blur closes WITHOUT taking the typed text. See the header: the
            // input is a filter, never the value.
            close();
          }}
          onKeyDown={onKeyDown}
        />

        {open ? (
          <ul
            id={listId}
            role="listbox"
            /*
             * DELIBERATELY UNNAMED. It used to carry `aria-label={label}`, the
             * same accessible name as the input — so two elements answered to
             * one field name, in a screen reader's element list and in
             * `getByLabel`. Shipped in 4.1; found in 4.2, the first time this
             * component landed on a field the form's field-search test drives
             * by label.
             *
             * `${label} options` does not fix it either: that name CONTAINS the
             * field's name, and a substring matcher still finds both. The popup
             * is transient, `aria-controls` already ties it to a combobox that
             * IS named, and every option carries its own text — so the name
             * here was never doing work that the combobox's was not.
             */
            className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-ctl border border-card-line bg-surface shadow-lg"
          >
            {shown.length === 0 ? (
              <li className="px-3 py-2 text-body text-ink2">{emptyLabel}</li>
            ) : (
              shown.map((option, index) => (
                <li
                  key={option.value}
                  id={`${id}-o${String(index)}`}
                  role="option"
                  aria-selected={option.value === value}
                  className={clsx(
                    'flex cursor-pointer items-baseline justify-between gap-3 px-3 py-2 text-body',
                    index === active ? 'bg-brand-50 text-ink' : 'text-ink',
                  )}
                  // `mousedown`, not `click`: the input's blur would close the
                  // list before a click could land on it.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    pick(option);
                  }}
                  onMouseEnter={() => {
                    setActive(index);
                  }}
                >
                  <span>{option.label}</span>
                  {option.detail === undefined ? null : (
                    <span className="shrink-0 text-chip text-ink2">{option.detail}</span>
                  )}
                </li>
              ))
            )}
            {/* WHAT IS NOT ON SCREEN SAYS SO (RM-052). The list is complete and
                the render is capped, which are different facts — and a reader
                who cannot see the difference is back to guessing whether the
                thing they want exists. `role="presentation"` because it is not
                an option. */}
            {hidden === 0 ? null : (
              <li role="presentation" className="px-3 py-2 text-meta text-ink2">
                {String(hidden)} more match — keep typing to narrow them
              </li>
            )}
          </ul>
        ) : null}
      </div>

      {/*
       * How many matched, politely — the same job the mention chooser gives a
       * live region, and the part of the pattern a sighted person gets from
       * seeing the list shrink as they type.
       */}
      <span role="status" className="sr-only">
        {open ? `${String(shown.length)} of ${String(options.length)} match` : ''}
      </span>

      {chosen !== null && clearLabel !== undefined ? (
        <button
          type="button"
          className="self-start rounded-ctl text-chip font-semibold text-brand-700 underline"
          onClick={() => {
            onChange(null);
          }}
        >
          {clearLabel}
        </button>
      ) : null}

      {hint !== undefined && error === undefined ? (
        <span className="text-chip text-ink2">{hint}</span>
      ) : null}
      {error !== undefined ? (
        <span role="alert" className="text-chip font-medium text-red-fg">
          {error}
        </span>
      ) : null}
    </div>
  );
}
